import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FailCard from "@/components/feature/FailCard";
import Reveal from "@/components/base/Reveal";
import usePageMeta from "@/hooks/usePageMeta";
import { articles } from "@/mocks/articles";
import { categories, siteInfo } from "@/mocks/siteData";

const sortOptions = [
  { value: "latest", label: "最新发布" },
  { value: "hot", label: "热度最高" },
  { value: "index", label: "翻车指数" },
];

export default function Category() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") ?? "all";
  const query = params.get("q") ?? "";
  const sort = params.get("sort") ?? "latest";
  const [keyword, setKeyword] = useState(query);

  const activeCategory = categories.find((item) => item.slug === activeCat);

  const list = useMemo(() => {
    let result = [...articles];
    if (activeCat !== "all") {
      result = result.filter((item) => item.categorySlug === activeCat);
    }
    if (query.trim()) {
      const needle = query.trim().toLowerCase();
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(needle) ||
          item.excerpt.toLowerCase().includes(needle) ||
          item.tags.some((tag) => tag.toLowerCase().includes(needle))
      );
    }
    if (sort === "index") result.sort((a, b) => b.failIndex - a.failIndex);
    if (sort === "hot") result.sort((a, b) => Number.parseFloat(b.views) - Number.parseFloat(a.views));
    if (sort === "latest") result.sort((a, b) => (a.date < b.date ? 1 : -1));
    return result;
  }, [activeCat, query, sort]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const pageTitle = activeCategory
    ? `${activeCategory.name}事件合集_${activeCategory.name}现场与回放 - 直播翻车观察局`
    : "全部翻车事件_直播事故与名场面回放合集 - 直播翻车观察局";

  const pageDesc = activeCategory
    ? `${activeCategory.desc} 收录${activeCategory.name}相关的翻车视频回放、关键截图与事件时间线，随时更新最新现场。`
    : "浏览全部网红翻车事件，按直播事故、带货翻车、连线失控、言论翻车与人设崩塌分类整理，含视频回放与关键截图。";

  const crumbs = [
    { label: "首页", to: "/" },
    ...(activeCategory ? [{ label: "全部事件", to: "/category" }, { label: activeCategory.name }] : [{ label: "全部事件" }]),
  ];

  usePageMeta({
    title: pageTitle,
    description: pageDesc,
    keywords: activeCategory
      ? `网红翻车${activeCategory.name},网红翻车现场,${activeCategory.name}视频回放,网红翻车截图`
      : "网红翻车,网红翻车现场,网红翻车直播事故,网红翻车视频回放,网红翻车截图,网红翻车名场面",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          name: activeCategory ? `${activeCategory.name}事件合集` : "全部网红翻车事件",
          description: pageDesc,
          inLanguage: "zh-CN",
          mainEntity: {
            "@type": "ItemList",
            itemListElement: list.slice(0, 10).map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.title,
              url: `https://fanche-observer.example/article/${item.id}`,
            })),
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: crumbs.map((crumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.label,
            item: crumb.to ? `https://fanche-observer.example${crumb.to}` : undefined,
          })),
        },
      ],
    },
  });

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-8 pb-6 bg-background-100 border-b border-background-200">
        <Breadcrumb items={crumbs} />
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-6 items-end">
          <div className="lg:col-span-2">
            <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950 leading-tight">
              {activeCategory ? `${activeCategory.name}现场合集` : "全部网红翻车事件"}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {activeCategory
                ? activeCategory.desc
                : "从直播事故到人设崩塌，按下方的分类与排序逐步缩小范围，每条都附有事件时间线、关键截图与复盘。"}
            </p>
          </div>
          <div className="lg:col-span-1 lg:text-right">
            <p className="text-sm text-foreground-500">
              共 <span className="font-heading font-bold text-2xl text-primary-600 mx-1">{list.length}</span>
              条整理
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-6 bg-background-50 border-b border-background-200 sticky top-[100px] z-30 backdrop-blur">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex flex-wrap gap-2 flex-1">
            <button
              type="button"
              onClick={() => updateParam("cat", "")}
              className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                activeCat === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 border border-background-300 text-foreground-700 hover:border-primary-300"
              }`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onClick={() => updateParam("cat", cat.slug)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                  activeCat === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 border border-background-300 text-foreground-700 hover:border-primary-300"
                }`}
              >
                <i className={cat.icon}></i>
                {cat.name}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                updateParam("q", keyword.trim());
              }}
              className="relative flex-1 sm:w-64"
            >
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索事件或关键词"
                className="w-full h-10 pl-10 pr-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </form>
            <select
              value={sort}
              onChange={(event) => updateParam("sort", event.target.value)}
              aria-label="排序方式"
              className="h-10 px-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {query && (
          <div className="mt-4 flex items-center gap-3 flex-wrap text-sm text-foreground-600">
            <span>
              关键词「<span className="text-primary-600 font-medium">{query}</span>」的搜索结果
            </span>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                updateParam("q", "");
              }}
              className="inline-flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
              清除
            </button>
          </div>
        )}
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
        {list.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {list.map((item, index) => (
              <Reveal key={item.id} delay={(index % 3) * 60} className="h-full">
                <FailCard {...item} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="py-20 text-center">
            <span className="inline-flex w-14 h-14 rounded-full bg-background-100 border border-background-200 items-center justify-center text-foreground-500 mb-4">
              <i className="ri-emotion-unhappy-line text-2xl"></i>
            </span>
            <h2 className="font-heading font-bold text-xl text-foreground-950">没有找到匹配的事件</h2>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词试试，或者直接浏览全部事件。
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setParams(new URLSearchParams(), { replace: true });
              }}
              className="mt-6 inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100 border-t border-background-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950">
              有更值得整理的事件？
            </h2>
            <p className="mt-1.5 text-sm text-foreground-600">
              把你看到的线索发给我们，编辑会核对后整理成完整复盘。
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-send-plane-line"></i>
              提供线索
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md border border-background-300 text-sm text-foreground-800 whitespace-nowrap hover:border-primary-400 hover:text-primary-600 transition-colors"
            >
              了解 {siteInfo.shortName}
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}